import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Seo } from "../components/Seo";
import { Footer } from "../components/Footer";
import { api } from "../lib/api";
import { track } from "../lib/analytics";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking", data: null });
  const tries = useRef(0);
  const fired = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState({ phase: "error" }); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (!fired.current) { fired.current = true; track("order_paid", { plan: data.plan, value: data.amount_aud, currency: "AUD", transaction_id: data.order_id }); }
          setState({ phase: "paid", data });
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setState({ phase: "failed", data }); return; }
      } catch { /* keep polling */ }
      if (++tries.current < 10) timer = setTimeout(poll, 2000); else setState({ phase: "timeout" });
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen grain relative" data-testid="payment-success-page">
      <Seo title="Payment status" description="Confirming your WanderlyMe plan payment." path="/payment/success" noindex />
      <Navbar />
      <main className="max-w-xl mx-auto px-6 py-24 text-center">
        {state.phase === "checking" && <><Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-muted-foreground" /><p className="font-display text-2xl" data-testid="payment-checking">Confirming your payment…</p></>}
        {state.phase === "paid" && (
          <div data-testid="payment-paid">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-[#1A3B2B]" strokeWidth={1.5} />
            <h1 className="font-display text-4xl mb-3">You're booked in.</h1>
            <p className="text-muted-foreground mb-2">Payment of <b>A${Number(state.data.amount_aud).toFixed(2)}</b> received for your {state.data.plan === "concierge" ? "Concierge" : "Verified"} Plan. A human review of your trip starts now — we'll deliver within 48 hours.</p>
            <p className="text-xs text-muted-foreground mb-8">Order {state.data.order_id} · a confirmation email is on its way.</p>
            <Link to="/trips" className="inline-flex px-6 py-3 rounded-full bg-[#1A3B2B] text-[#FDFBF7]" data-testid="payment-paid-cta">Back to my trips</Link>
          </div>
        )}
        {(state.phase === "failed" || state.phase === "timeout" || state.phase === "error") && (
          <div data-testid="payment-problem">
            <XCircle className="w-12 h-12 mx-auto mb-4 text-[#C8553D]" strokeWidth={1.5} />
            <h1 className="font-display text-3xl mb-3">{state.phase === "timeout" ? "Still confirming" : "Payment not completed"}</h1>
            <p className="text-muted-foreground mb-8">{state.phase === "timeout" ? "Your bank may still be processing. If you were charged, you'll get a confirmation email shortly — or contact hello@wanderlyme.com." : "No charge was made. You can try again from the plans page."}</p>
            <Link to="/plans" className="inline-flex px-6 py-3 rounded-full border border-[#1A3B2B]">Back to plans</Link>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
